import { useCallback, useEffect, useMemo, useState } from "react";
//formik
import { Form, FormikProvider, useFormik } from "formik";
// mui components
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import { LoadingButton } from "@mui/lab";
import { queryKeys } from "src/react-query/queryKeys";

// custom components, services and constants
import { BaseTable } from "src/components/BaseTable/BaseTable";
import {
  userFormValidationSchema,
} from "src/util/validationSchemas";
import { Loader } from "src/components/Loader";
import Countries from "src/data/countries.json";
import {
  getUserProfile,
} from "src/react-query/endPoints";
import enums from "src/util/enums";
import {
  getUserProfileDefaultValues,
  profileHeaderBar,
  roleColumns,
} from "./UserProfile.constant";
//actions
import { alertSlice } from "src/store/alert/alert.slice";

// hooks
import { useMutate } from "src/hooks/useFetch.hook";
import { useAccess } from "src/hooks/useAccess.hook";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Card,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { RootTabItemStyles } from "src/theme/overrides/Tabs";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { styled } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import sanitizeValues from "src/utils/sanitizeValues";
import { fetchWithError } from "src/react-query/fetchWithError";
import { selector } from "src/react-query/select";
import { subscriptionDetailsColumns } from "src/components/Team/teams.constants";
import SubscriptionDetailsModal from "src/components/Team/SubscriptionDetailsModal";

const RootTabStyle = styled(TabList)(({ theme, length }) => ({
  width: "98%",
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
  "& .MuiTabs-flexContainer": {
    justifyContent: "left",
  },
  "& .MuiTab-root": {
    width: `${100 / length}%`,
  },
}));


function UserProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isReadOnly = useAccess("FA");
  const { mutateAsync, isLoading } = useMutate("user");
  const authUser = useSelector((state) => state.auth.user);
  const data = authUser;
  const isFetching = !authUser;
  const userCompanyType = authUser?.company?.companyType;
  const showLicenseTab = userCompanyType === "P" || userCompanyType === "T";

  const tabsToShow = useMemo(() => {
    const tabs = [...profileHeaderBar];
    if (showLicenseTab) tabs.push({ value: "charger-licenses", label: "Charger Licenses" });
    return tabs;
  }, [showLicenseTab]);

  const [selectedTab, setSelectedTab] = useState(tabsToShow[0].value);
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");

  // Charger licenses tab state
  const [selectedLicenseTeam, setSelectedLicenseTeam] = useState(null);
  const [licenseSubscriptionData, setLicenseSubscriptionData] = useState([]);
  const [licenseSubscriptionModalOpen, setLicenseSubscriptionModalOpen] = useState(false);
  const [selectedLicenseSubscriptionRow, setSelectedLicenseSubscriptionRow] = useState(null);

  // Auto-select the first team once profile data loads
  useEffect(() => {
    if (!isFetching && data?.role?.length && !selectedLicenseTeam) {
      setSelectedLicenseTeam(data.role[0]);
    }
  }, [isFetching, data, selectedLicenseTeam]);

  const { data: licenseApiData, isFetching: isTeamDetailsFetching } = useQuery(
    queryKeys.profileChargerLicense(selectedLicenseTeam?.teamId),
    fetchWithError,
    { enabled: selectedTab === "charger-licenses" && !!selectedLicenseTeam?.teamId, select: selector }
  );

  useEffect(() => {
    if (!selectedLicenseTeam) { setLicenseSubscriptionData([]); return; }
    if (!licenseApiData) return;
    const licenses = Array.isArray(licenseApiData) ? licenseApiData : licenseApiData?.chargerLicenses ?? [];
    if (licenses.length) {
      setLicenseSubscriptionData(
        licenses.map((license, index) => ({
          id: license?.licensePk || index + 1,
          licensePk: license?.licensePk,
          licenseId: license?.licenseId,
          status: license?.status,
          chargerId: license?.chargeBoxId,
          chargerCreationDate: license?.createdAt,
          chargerSubscriptionExpiryDate: license?.expiry,
          duration: license?.duration,
          final_expiry: license?.final_expiry,
          contractSummary: license?.summary,
          remarks: license?.remarks,
          chargerType: license?.chargerType,
        }))
      );
    } else {
      setLicenseSubscriptionData([]);
    }
  }, [licenseApiData, selectedLicenseTeam]);

  const handleLicenseSubscriptionDetailsOpen = useCallback((row) => {
    setSelectedLicenseSubscriptionRow(row);
    setLicenseSubscriptionModalOpen(true);
  }, []);

  const handleLicenseSubscriptionDetailsClose = useCallback(() => {
    setLicenseSubscriptionModalOpen(false);
    setSelectedLicenseSubscriptionRow(null);
  }, []);



  useEffect(() => {
    if (tabFromUrl) {
      const isValidTab = tabsToShow.some(
        (tab) => tab.value === tabFromUrl
      );
      if (isValidTab) {
        setSelectedTab(tabFromUrl);
      }
    }
  }, [tabFromUrl, tabsToShow]);

  const onSubmit = async (values) => {
    const {
      actions: { setAlert },
    } = alertSlice;

    const sanitizeData = sanitizeValues(values);
    const {
      id,
      zipCode,
      street,
      country,
      houseNumber,
      username,
      email,
      lastName,
      firstName,
      city,
      language,
    } = sanitizeData;
    const profileReqBody = {
      username,
      email,
      firstName,
      lastName,
      language,
      id,
      address: {
        zipCode,
        street,
        country,
        houseNumber,
        city,
        addressId: null,
      },
    };
    await mutateAsync(
      {
        url: getUserProfile(),
        method: "PUT",
        body: profileReqBody,
      },
      {
        onSuccess: () => {
          dispatch(
            setAlert({
              open: true,
              message: "Profile Updated Successfully",
              severity: enums.SEVERITY.SUCCESS,
            })
          );
          queryClient.invalidateQueries(queryKeys.user());
        },
        onError: (error) => {
          dispatch(
            setAlert({
              open: true,
              message: error?.response?.data?.message ?? error?.message,
              severity: enums.SEVERITY.ERROR,
            })
          );
        },
      }
    );
  };

  const formik = useFormik({
    initialValues: getUserProfileDefaultValues,
    validationSchema: userFormValidationSchema,
    onSubmit,
  });

  const { errors, touched, handleSubmit, getFieldProps, setFieldValue } =
    formik;

  useEffect(() => {
    if (!isFetching && data) {
      const profileData = { ...data, ...data.address };
      delete profileData.company;
      delete profileData.role;
      for (let [k, v] of Object.entries(profileData)) {
        "role" !== k && setFieldValue(k, v);
      }
    }
  }, [data, isFetching]);

  const rowOrCellClickRedirect = ({ id }) => {
    navigate(`/team/${id}`);
  };
  if (isFetching) {
    return <Loader />;
  }

  const handleTabChange = (_, tab) => {
    setSelectedTab(tab);
  };

  return (
    <TabContext value={selectedTab}>
      <Stack
        direction="row"
        justifyContent="center"
        alignItem="center"
        sx={{ width: "100%" }}
      >
        <RootTabStyle
          indicatorColor="transparent"
          centered
          length={tabsToShow.length}
          onChange={handleTabChange}
        >
          {tabsToShow.map((tab) => (
            <RootTabItemStyles value={tab.value} label={tab.label} />
          ))}
        </RootTabStyle>
      </Stack>
      <Card sx={{ p: 4 }}>
        <TabPanel value="general">
          <FormikProvider value={formik}>
            <Form
              onSubmit={handleSubmit}
              autoComplete="off"
              id="profile"
              noValidate
            >
              <Grid container spacing={3}>
                <Grid item xs={12} lg={12}>
                  <Typography variant="subtitle1" className="mb-2">
                    User Detail
                  </Typography>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <TextField
                      disabled
                      label="Username"
                      size="small"
                      {...getFieldProps("username")}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} lg={12}>
                  <Typography variant="subtitle1" className="mb-2">
                    User Info
                  </Typography>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label="First Name"
                      size="small"
                      {...getFieldProps("firstName")}
                      error={Boolean(touched.firstName && errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label="Last Name"
                      size="small"
                      {...getFieldProps("lastName")}
                      error={Boolean(touched.lastName && errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label="Email"
                      size="small"
                      {...getFieldProps("email")}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label="Phone"
                      size="small"
                      {...getFieldProps("phone")}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={3} className="mb-5">
                <Grid item xs={12} lg={12}>
                  <Typography
                    variant="subtitle1"
                    className="mb-2 text-uppercase"
                  >
                    User Address
                  </Typography>
                  <Divider className="text-muted" />
                </Grid>
                <Grid item xs={6} lg={6}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label="Street"
                      size="small"
                      disabled={isReadOnly}
                      {...getFieldProps("street")}
                      error={Boolean(touched.street && errors.street)}
                      helperText={touched.street && errors.street}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6} lg={6}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label="House number"
                      size="small"
                      disabled={isReadOnly}
                      {...getFieldProps("houseNumber")}
                      error={Boolean(touched.houseNumber && errors.houseNumber)}
                      helperText={touched.houseNumber && errors.houseNumber}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6} lg={6}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label="Zip code"
                      size="small"
                      disabled={isReadOnly}
                      {...getFieldProps("zipCode")}
                      error={Boolean(touched.zipCode && errors.zipCode)}
                      helperText={touched.zipCode && errors.zipCode}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6} lg={6}>
                  <FormControl fullWidth size="small">
                    <TextField
                      label="City"
                      size="small"
                      disabled={isReadOnly}
                      {...getFieldProps("city")}
                      error={Boolean(touched.city && errors.city)}
                      helperText={touched.city && errors.city}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6} lg={6}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={Boolean(touched.country && errors.country)}
                  >
                    <InputLabel id="country">Country</InputLabel>
                    <Select
                      labelId="country"
                      id="country"
                      disabled={isReadOnly}
                      label="Country"
                      {...getFieldProps("country")}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {Countries.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} lg={12}>
                  <Typography variant="subtitle1" className="mb-2">
                    User Roles
                  </Typography>
                  <Divider />
                </Grid>
                <Grid item xs={12} lg={12}>
                  <BaseTable
                    rows={data?.role ?? []}
                    columns={roleColumns}
                    loading={isFetching}
                    getRowId={(row) => row.teamId}
                    rowOrCellClickRedirect={rowOrCellClickRedirect}
                  />
                </Grid>
              </Grid>
              <Grid container mt={2}>
                <Grid item xs={6} lg={6}>
                  <LoadingButton
                    size="large"
                    type="submit"
                    variant="contained"
                    disabled={isReadOnly}
                    loading={isLoading}
                  >
                    Update
                  </LoadingButton>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </TabPanel>

        {showLicenseTab && (
          <TabPanel value="charger-licenses">
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  size="small"
                  options={data?.role ?? []}
                  getOptionLabel={(option) => option.teamName ?? ""}
                  isOptionEqualToValue={(option, value) => option.teamId === value.teamId}
                  value={selectedLicenseTeam}
                  onChange={(_, newValue) => setSelectedLicenseTeam(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Team" size="small" />
                  )}
                />
              </Grid>
            </Grid>
            <BaseTable
              rows={licenseSubscriptionData}
              columns={subscriptionDetailsColumns(
                handleLicenseSubscriptionDetailsOpen,
                false,
                null,
                false
              )}
              getRowId={(row) => row.id}
              loading={isTeamDetailsFetching}
              pageSize={15}
            />
          </TabPanel>
        )}
      </Card>

      <SubscriptionDetailsModal
        open={licenseSubscriptionModalOpen}
        onClose={handleLicenseSubscriptionDetailsClose}
        subscriptionData={selectedLicenseSubscriptionRow}
      />
    </TabContext>
  );
}

export default UserProfile;
